import React, { createContext, useContext } from "react";
import i18n from "@/i18n"; // Import the configured i18n instance

/**
 * @name TranslationContext
 * @description A context for providing translation functionality to child components.
 * This implementation provides a simple translation function using i18next.
 * @type {React.Context}
 */
export const TranslationContext = createContext({
  t: (key) => key,
});

/**
 * @name TranslationProvider
 * @description A context provider component that provides translation functionality to child components.
 * @param {ReactNode} children - The child components that will have access to the translation context.
 */
export const TranslationProvider = ({ children }) => {
  // Translation function using i18next
  const t = (key) => {
    return i18n.t(key);
  };

  return (
    <TranslationContext.Provider value={t}>
      {children}
    </TranslationContext.Provider>
  );
};

/**
 * @name useTranslationContext
 * @description A custom hook to access the translation function from TranslationContext.
 * @returns {function} The translation function.
 */
export const useTranslationContext = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error("useTranslationContext must be used within a TranslationProvider");
  }
  return context;
};
